import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from './telemetry';

/**
 * Static crash copy in every supported language (i18n itself may be what failed, so no
 * translation files here). Shown as "<current language> · English".
 */
const EN = { message: 'Something went wrong.', reload: 'Reload' };
export const CRASH_TEXT: Record<string, { message: string; reload: string }> = {
  en: EN,
  vi: { message: 'Đã xảy ra lỗi.', reload: 'Tải lại' },
  ko: { message: '문제가 발생했습니다.', reload: '새로고침' },
  zh: { message: '出现错误。', reload: '重新加载' },
  ja: { message: '問題が発生しました。', reload: '再読み込み' },
};

/** Current UI language without i18next: <html lang> (kept in sync by i18n.ts), else vi. */
export function crashLanguage(): string {
  const lang = (typeof document !== 'undefined' ? document.documentElement.lang : '') || 'vi';
  const short = lang.slice(0, 2).toLowerCase();
  return short in CRASH_TEXT ? short : 'vi';
}

/**
 * Last-resort render crash boundary around the router: reports the crash (telemetry beacon) and
 * shows a plain reload prompt instead of a white screen. Text is static (i18n may be what failed).
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError('render', error, { component: (info.componentStack ?? '').slice(0, 280) });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const lng = crashLanguage();
    const local = CRASH_TEXT[lng] ?? EN;
    const both = (k: 'message' | 'reload') => (local === EN ? EN[k] : `${local[k]} · ${EN[k]}`);
    return (
      <main
        className="page"
        role="alert"
        lang={lng}
        style={{ textAlign: 'center', paddingTop: 48 }}
      >
        <p>{both('message')}</p>
        <button type="button" className="ui-btn ui-btn--primary" onClick={() => location.reload()}>
          {both('reload')}
        </button>
      </main>
    );
  }
}
